/**
 * Logger utility for consistent logging across the application
 * Uses log4j-style logging levels
 */

// Define log levels
const LOG_LEVELS = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5
};

// Current log level (can be changed at runtime)
let currentLogLevel = LOG_LEVELS.INFO;

// Whether to include timestamps in logs
let includeTimestamp = true;

// Whether to include log level in logs
let includeLogLevel = true;

/**
 * Format a log message with optional timestamp and level
 * 
 * @param {string} message - The log message
 * @param {string} level - The log level
 * @returns {string} - Formatted log message
 */
const formatLogMessage = (message, level) => {
  let formattedMessage = '';
  
  if (includeTimestamp) {
    formattedMessage += `[${new Date().toISOString()}] `;
  }
  
  if (includeLogLevel) {
    formattedMessage += `[${level}] `;
  }
  
  formattedMessage += message;
  return formattedMessage;
};

/**
 * Main logger object with methods for each log level
 */
export const logger = {
  /**
   * Set the current log level
   * 
   * @param {string} level - Log level name (TRACE, DEBUG, INFO, WARN, ERROR, FATAL)
   */
  setLogLevel: (level) => {
    if (LOG_LEVELS[level] !== undefined) {
      currentLogLevel = LOG_LEVELS[level];
    } else {
      console.warn(`Invalid log level: ${level}`);
    }
  },
  
  /**
   * Get the current log level name
   * 
   * @returns {string} - Current log level name
   */
  getLogLevel: () => {
    return Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === currentLogLevel);
  },
  
  /**
   * Configure logger options
   * 
   * @param {Object} options - Logger options
   * @param {boolean} options.includeTimestamp - Whether to include timestamps in logs
   * @param {boolean} options.includeLogLevel - Whether to include log level in logs
   */
  configure: (options = {}) => {
    if (options.includeTimestamp !== undefined) {
      includeTimestamp = options.includeTimestamp;
    }
    
    if (options.includeLogLevel !== undefined) {
      includeLogLevel = options.includeLogLevel;
    }
  },
  
  /**
   * Log a trace message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  trace: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.TRACE) {
      console.log(formatLogMessage(message, 'TRACE'), data);
    }
  },
  
  /**
   * Log a debug message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  debug: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.DEBUG) {
      console.log(formatLogMessage(message, 'DEBUG'), data);
    }
  },
  
  /**
   * Log an info message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  info: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.INFO) {
      console.log(formatLogMessage(message, 'INFO'), data);
    }
  },
  
  /**
   * Log a warning message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  warn: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.WARN) {
      console.warn(formatLogMessage(message, 'WARN'), data);
    }
  },
  
  /**
   * Log an error message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  error: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.ERROR) {
      console.error(formatLogMessage(message, 'ERROR'), data);
    }
  },
  
  /**
   * Log a fatal error message
   * 
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  fatal: (message, data = {}) => {
    if (currentLogLevel <= LOG_LEVELS.FATAL) {
      console.error(formatLogMessage(message, 'FATAL'), data);
    }
  }
};
